import { css } from 'styled-components';

import { THIN_SCROLLBAR } from './scrollbar';

/**
 * The app's one menu schema.
 *
 * Four surfaces had grown their own version of "a floating panel with a column
 * of rows" — the sidebar account menu, the composer's model picker, the
 * settings dropdowns, and the settings nav. They agreed on the idea and
 * disagreed on every number: panel backgrounds split between "--bg-color-page"
 * and "--bg-color-container", row radii between 4px and 6px, row text between
 * 0.75rem and 13px, row padding four different ways. Individually invisible;
 * together they read as several products stitched into one, which is exactly
 * what you notice when two of them are open at once.
 *
 * So the values live here once and every menu consumes them. Adding a menu
 * should mean picking a behaviour (a "<button>", a Radix item, a nav row), not
 * re-deciding what a menu looks like.
 *
 * The account menu is the reference the rest were tuned to: it is the oldest,
 * the most seen, and the one that already looked right.
 *
 * These are "css" fragments rather than components on purpose. The four call
 * sites wrap genuinely different elements — a plain button, a Radix
 * "DropdownMenu.Item", a Radix "RadioItem", a nav button — and a shared
 * component would have to be generic enough to be worse than all four.
 */

/** Row text. One size for every menu row in the app. */
export const MENU_ROW_TYPE = css`
  font-size: 0.75rem;
  line-height: 1.125rem;
  font-weight: 400;
`;

/**
 * The floating panel: the card a menu's rows sit on.
 *
 * "--bg-color-container" is the "raised surface" token, and a menu is exactly
 * that — it floats above whatever it was opened from. It also has to work over
 * two different grounds (the page, and the settings dialog, which is itself a
 * raised surface), which is why the border and shadow do the separating rather
 * than the fill alone.
 *
 * Callers supply their own positioning and "min-width"; those are the parts
 * that legitimately differ between a right-click menu, a dropdown anchored to
 * a trigger, and a sidebar popover.
 */
export const MENU_SURFACE = css`
  display: flex;
  flex-direction: column;
  padding: 4px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-color-container);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
  overflow-y: auto;
  overscroll-behavior: none;
  ${THIN_SCROLLBAR}
`;

/**
 * One row.
 *
 * "min-height" rather than a fixed height so a row that wraps (a long model
 * name, a translated label) grows instead of clipping — the height is a floor
 * for the tap target, not a promise about the text.
 */
export const MENU_ROW = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 28px;
  padding: 5px 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  outline: none;
  user-select: none;
  ${MENU_ROW_TYPE}

  /* "data-highlighted" is how Radix marks the keyboard-focused row; plain
     buttons only ever get :hover. Both land on the same fill so keyboard and
     pointer users see the same thing. */
  &[data-highlighted],
  &:hover:not(:disabled):not([data-disabled]) {
    background: var(--hover-bg);
  }

  &:disabled,
  &[data-disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: -2px;
  }
`;

/**
 * A row's leading glyph.
 *
 * Muted by default so a column of rows reads as a list of words with marks
 * beside them, rather than a column of icons competing with their own labels.
 * 16px matches the sidebar icon set, which is where most of these come from.
 */
export const MENU_ROW_ICON = css`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--text-label);

  svg {
    width: 16px;
    height: 16px;
  }
`;

/**
 * Hairline between groups. Groups exist so a destructive action never sits
 * flush against a routine one — "Log Out" one careless pixel from "Settings"
 * is how people log themselves out by accident.
 */
export const MENU_SEPARATOR = css`
  height: 1px;
  margin: 4px 6px;
  background: var(--divider-color);
`;

/**
 * Right-aligned keyboard hint inside a row. "margin-left: auto" rather than a
 * fixed column so it stays flush right whatever the label length.
 */
export const MENU_SHORTCUT = css`
  margin-left: auto;
  padding-left: 16px;
  display: flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
`;

/**
 * One key of an accelerator hint, drawn as a cap.
 *
 * Each key gets its own cap so a punctuation key reads as a key. "Ctrl+,"
 * as bare text ends in a comma that looks like the sentence's, not like
 * something to press.
 */
export const MENU_KEY = css`
  min-width: 16px;
  padding: 1px 4px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--button-color-dark);
  color: var(--text-label);
  font-size: 0.6875rem;
  line-height: 14px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;
